import { container } from './di/container';
import { InputManager } from './input-manager';
import { Tree } from './tree';
import { Pit } from './pit';
import { Direction, AnimationType } from './animations/types';
import { AnimationManager } from './animations/animation-manager';
import { AnimationLoader } from './animations/animation-loader';
import { CAT_ANIMATION_MAP } from './animations/animation-config';
import { Animal } from './animal';
import { ICollidable, Bounds, CollisionUtils } from './collision/types';
import { SoundId } from './audio/sound-manager';
import { GameTime } from './game-time';

export class Cat extends Animal {
	// ICollidable override
	override readonly collisionType = 'cat';

	// Movement constants (Cat-specific)
	private static readonly ANALOG_DEADZONE = 0.15;
	private static readonly RUN_THRESHOLD = 0.7;
	private static readonly SLOW_WALK_MULTIPLIER = 0.5;
	private static readonly CLIMB_SPEED = 0.8;
	private static readonly DESCEND_SPEED = 0.4;
	private static readonly LANDING_DELAY_MULTIPLIER = 1.2;
	private static readonly PIT_SLOW_DISTANCE = 40; // Pit kenarına bu kadar yakınken yavaşla
	private static readonly PIT_MIN_SPEED_FACTOR = 0.25; // Minimum hız faktörü

	vx: number;
	vy: number;
	speed: number;
	sneakSpeed: number;
	jumpSpeed: number;
	gravity: number;
	isMoving: boolean;
	isJumping: boolean;
	isGrounded: boolean;
	isCrouching: boolean;
	isAttacking: boolean;
	attackMomentum: number; // Store momentum when attack starts
	isHurt: boolean;
	isClimbing: boolean;
	isLanding: boolean;
	isPlayingDeathAnimation: boolean;
	isDeathAnimationComplete: boolean;
	isInPit: boolean;
	currentPitBounds: Bounds | null;
	nearbyPitBounds: Bounds | null; // Pit bounds for proximity slowdown
	climbingTree: Tree | null;
	groundY: number;
	canJump: boolean;
	justStoppedClimbing: boolean;
	idleBlinkCounter: number;
	idleBlinkChance: number;
	inputManager: InputManager;
	animationManager: AnimationManager;
	frameSpeed: number;
	attackFrameSpeed: number;
	isCollidingWithTree: boolean;
	activeTree: Tree | null;
	originalGroundY: number;
	noInputTimer: number;
	sitCount: number;
	isSleeping: boolean;
	private readonly NO_INPUT_THRESHOLD_SECONDS = 5;

	// Callbacks for actions
	onPoop?: (x: number, y: number) => void;
	private readonly SIT_ANIMATION_CHANCE = 0.3; // %30 şans

	constructor(x: number, y: number) {
		super(x, y, 9); // 9 health points

		// Override sprite dimensions from Animal
		this.spriteWidth = 40;
		this.spriteHeight = 40;
		this.spriteHorizontalOffset = 18;

		// Override collision box from Animal
		this.collisionWidth = 24;
		this.collisionHeight = 20;
		this.collisionOffsetX = (this.spriteWidth - this.collisionWidth) / 2;
		this.collisionOffsetY = 14;

		this.vx = 0;
		this.vy = 0;
		this.speed = 3;
		this.sneakSpeed = 0.8;
		this.jumpSpeed = -9;
		this.gravity = 0.6;
		this.isMoving = false;
		this.isJumping = false;
		this.isGrounded = true;
		this.isCrouching = false;
		this.isAttacking = false;
		this.attackMomentum = 0;
		this.isHurt = false;
		this.isClimbing = false;
		this.isLanding = false;
		this.isPlayingDeathAnimation = false;
		this.isDeathAnimationComplete = false;
		this.isInPit = false;
		this.currentPitBounds = null;
		this.nearbyPitBounds = null;
		this.climbingTree = null;
		this.groundY = y;
		this.originalGroundY = y;
		this.idleBlinkCounter = 0;
		this.idleBlinkChance = 180; // Her 180 frame'de bir blink şansı (yaklaşık 3 saniye @ 60fps)
		this.noInputTimer = 0;
		this.sitCount = 0;
		this.isSleeping = false;
		this.canJump = true;
		this.justStoppedClimbing = false;
		this.inputManager = container.resolve(InputManager);
		this.animationManager = new AnimationManager();
		this.frameSpeed = 5;
		this.attackFrameSpeed = 2;
		this.isCollidingWithTree = false;
		this.activeTree = null;

		// Sound configuration
		this.sounds = {
			attack: SoundId.CAT_ATTACK,
		};
	}

	async loadSprites(): Promise<void> {
		const animations = await AnimationLoader.loadAnimations(CAT_ANIMATION_MAP);
		this.animationManager.setAnimations(animations);
	}

	private startClimbing(tree: Tree): void {
		this.isClimbing = true;
		this.climbingTree = tree;
		this.vy = 0;
		this.vx = 0;
		this.isGrounded = false;
		this.isMoving = false;
		this.isJumping = false;
		this.setAnimation('wallgrab');
		const treeBounds = tree.getBounds();
		// Direction'ı yaklaşma yönüne göre ayarla (x ayarlanmadan önce kontrol et)
		if (this.x < treeBounds.x) {
			this.animationManager.direction = 'east'; // Soldan yaklaşıyor, ağaca bak
		} else {
			this.animationManager.direction = 'west'; // Sağdan yaklaşıyor, ağaca bak
		}
		// X pozisyonunu ağacın kenarlarına göre ayarla
		// Sprite'ın kenarlarındaki boşluğu offset ile düzelt
		if (this.animationManager.direction === 'east') {
			// Sağa bakıyor, ağacın solunda - offset ile yakınlaştır
			this.x = treeBounds.x - this.spriteWidth + this.spriteHorizontalOffset;
		} else {
			// Sola bakıyor, ağacın sağında - offset ile yakınlaştır
			this.x = treeBounds.x + treeBounds.width - this.spriteHorizontalOffset;
		}

		// Kedinin sprite'ının alt kenarını ağacın gövdesine hizala
		// Eğer kedi ağacın üstündeyse, sprite'ın alt kenarını ağacın üst kenarına hizala
		// Eğer kedi ağacın altındaysa veya içindeyse, mevcut y pozisyonunu koru ama gövde sınırları içinde tut
		if (this.y + this.spriteHeight < treeBounds.y) {
			this.y = treeBounds.y - this.spriteHeight;
		} else {
			const maxY = treeBounds.y + treeBounds.height - this.spriteHeight;
			if (this.y > maxY) {
				this.y = maxY;
			}
			const minY = treeBounds.y - this.spriteHeight;
			if (this.y < minY) {
				this.y = minY;
			}
		}
	}

	private stopClimbing(): void {
		this.isClimbing = false;
		this.climbingTree = null;
		this.vx = 0;
		this.justStoppedClimbing = true; // Tırmanmayı bıraktığımızı işaretle
		this.startFalling(); // Düşme animasyonunu başlat
	}

	private turn(newDirection: Direction): void {
		this.animationManager.turn(newDirection);
	}

	private setAnimation(name: AnimationType, resetFrame = true): void {
		this.animationManager.currentAnimation = name;
		if (resetFrame) {
			this.animationManager.currentFrame = 0;
			this.animationManager.frameCounter = 0;
		}
	}

	private startFalling(): void {
		this.isGrounded = false;
		this.isJumping = false;
		this.setAnimation('fall');
	}

	private updatePitState(): void {
		if (this.isInPit) {
			// Keep cat in pit - use stored bounds
			if (this.currentPitBounds) {
				const pitBottom = this.currentPitBounds.y + this.currentPitBounds.height;
				this.groundY = pitBottom - this.spriteHeight;
			}
		} else {
			// Not in pit - reset to normal ground
			this.groundY = this.originalGroundY;
		}
	}

	/** Returns true if climbing logic handled the update (early return) */
	private updateClimbing(upPressed: boolean, downPressed: boolean): boolean {
		const tree = this.activeTree;
		if (!this.isClimbing || !tree) return false;

		const treeBounds = tree.getBounds();
		const climbSpeed = Cat.CLIMB_SPEED;
		const descendSpeed = Cat.DESCEND_SPEED;

		// Tırmanma sırasında havada olduğunu belirt
		this.isGrounded = false;
		this.isJumping = false; // Tırmanma zıplama değil

		if (upPressed) {
			this.y -= climbSpeed;
			this.animationManager.currentAnimation = 'wallclimb';
			if (this.y < treeBounds.y) {
				// Üste ulaştı, zorlanma animasyonu çalıştır
				this.y = treeBounds.y;
				this.animationManager.currentAnimation = 'ledgestruggle';
			}
		} else if (downPressed) {
			// Aşağı tuşuna basınca animasyonu geri sardır (frame'i azalt)
			this.y += descendSpeed;
			this.animationManager.currentAnimation = 'wallclimb';
			// Frame'i geri sardır
			const frames = this.animationManager.getFrames();
			if (frames.length > 0) {
				const speed = this.frameSpeed;
				this.animationManager.frameCounter--;
				if (this.animationManager.frameCounter < 0) {
					this.animationManager.currentFrame =
						(this.animationManager.currentFrame - 1 + frames.length) % frames.length;
					this.animationManager.frameCounter = speed - 1;
				}
			}
			// Gövdenin altına inince tırmanmayı bırak ve düş
			if (this.y + this.spriteHeight > treeBounds.y + treeBounds.height) {
				this.stopClimbing();
				return true;
			}
			// Üst sınırı kontrol et
			if (this.y < treeBounds.y) {
				this.y = treeBounds.y;
			}
		} else {
			if (this.animationManager.currentAnimation !== 'wallgrab') {
				this.animationManager.currentAnimation = 'wallgrab';
				// Animasyon değiştiğinde frame'i modüle et
				const frames = this.animationManager.getFrames();
				if (frames && frames.length > 0) {
					this.animationManager.currentFrame = this.animationManager.currentFrame % frames.length;
				}
			}
		}

		// Kediyi gövdenin yanına yerleştir, gövde boyunca hareket ettir
		// Üst sınırı kontrol et
		if (this.y < treeBounds.y) {
			this.y = treeBounds.y;
		}
		// X pozisyonu tırmanma başladığında ayarlandı, burada tekrar güncelleme
		this.vy = 0;
		this.vx = 0; // Yatay hareketi sıfırla
		// Frame counter'ı güncelle (tırmanma animasyonu için - sadece yukarı çıkarken veya idle)
		if (!downPressed) {
			// Atak animasyonu için özel frame counter
			if (this.isAttacking) {
				const frames = this.animationManager.getFrames();
				if (frames.length > 0) {
					const speed = this.attackFrameSpeed;
					this.animationManager.frameCounter++;
					if (this.animationManager.frameCounter >= speed) {
						const maxFrame = this.isCrouching ? frames.length - 2 : frames.length - 1;
						if (this.animationManager.currentFrame >= maxFrame) {
							this.isAttacking = false;
							this.animationManager.currentFrame = 0;
						} else {
							this.animationManager.currentFrame++;
						}
						this.animationManager.frameCounter = 0;
					}
				}
			} else {
				const frames = this.animationManager.getFrames();
				if (frames.length > 0) {
					if (
						this.animationManager.currentAnimation === 'wallclimb' ||
						this.animationManager.currentAnimation === 'wallgrab' ||
						this.animationManager.currentAnimation === 'ledgestruggle'
					) {
						const speed = this.frameSpeed;
						this.animationManager.frameCounter++;
						if (this.animationManager.frameCounter >= speed) {
							this.animationManager.currentFrame =
								(this.animationManager.currentFrame + 1) % frames.length;
							this.animationManager.frameCounter = 0;
						}
					}
				}
			}
		}
		return true;
	}

	private updateMovement(): void {
		// Tırmanma, landing ve ölme sırasında yatay hareket yok
		// Havadayken atak yapıldığında yatay hareket devam eder
		if (
			(!this.isAttacking || !this.isGrounded) &&
			!this.isClimbing &&
			!this.isLanding &&
			!this.isPlayingDeathAnimation
		) {
			// Pit kenarına yakınken hareket hızını azalt
			let effectiveVx = this.vx;
			if (this.nearbyPitBounds && this.isGrounded && !this.isInPit) {
				const speedFactor = this.calculatePitProximityFactor();
				effectiveVx = this.vx * speedFactor;
			}

			this.x += effectiveVx;
			// Çukurun içindeyse çukurun duvarlarına takıl
			if (this.isInPit && this.currentPitBounds) {
				this.x = constrain(
					this.x,
					this.currentPitBounds.x,
					this.currentPitBounds.x + this.currentPitBounds.width - this.spriteWidth
				);
			} else {
				// Normal sınırlar
				this.x = constrain(this.x, 0, width - this.spriteWidth);
			}
		}
	}

	/**
	 * Calculate speed factor based on proximity to pit edge (1.0 = full speed, 0.25 = min speed)
	 */
	private calculatePitProximityFactor(): number {
		if (!this.nearbyPitBounds) return 1.0;

		const catCenterX = this.x + this.spriteWidth / 2;
		const pitLeft = this.nearbyPitBounds.x;
		const pitRight = this.nearbyPitBounds.x + this.nearbyPitBounds.width;

		// Distance to nearest pit edge
		let distanceToEdge: number;
		const movingRight = this.vx > 0;
		const movingLeft = this.vx < 0;

		if (movingRight && catCenterX < pitLeft) {
			// Moving towards left edge of pit
			distanceToEdge = pitLeft - catCenterX;
		} else if (movingLeft && catCenterX > pitRight) {
			// Moving towards right edge of pit
			distanceToEdge = catCenterX - pitRight;
		} else {
			// Not moving towards pit or already past it
			return 1.0;
		}

		// If within slow distance, calculate factor
		if (distanceToEdge < Cat.PIT_SLOW_DISTANCE) {
			// Lerp from MIN_SPEED_FACTOR (at edge) to 1.0 (at PIT_SLOW_DISTANCE)
			const t = distanceToEdge / Cat.PIT_SLOW_DISTANCE;
			return Cat.PIT_MIN_SPEED_FACTOR + t * (1.0 - Cat.PIT_MIN_SPEED_FACTOR);
		}

		return 1.0;
	}

	private updateGravity(): void {
		if (this.isJumping || !this.isGrounded) {
			// Çukurun dibindeyse yukarı çıkamaz
			if (this.isInPit && this.isPlayingDeathAnimation) {
				// Çukurun dibinde kal, yukarı çıkma
				this.vy = 0;
				if (this.y < this.groundY) {
					this.y += this.gravity;
					if (this.y >= this.groundY) {
						this.y = this.groundY;
					}
				} else {
					this.y = this.groundY;
				}
			} else {
				this.vy += this.gravity;
				this.y += this.vy;
			}

			if (this.y >= this.groundY) {
				this.y = this.groundY;
				this.vy = 0;
				this.isJumping = false;
				this.isGrounded = true;
				this.canJump = true;
				this.justStoppedClimbing = false; // Yere düşünce tekrar tırmanabilir
				// Eğer çukurun dibindeyse direkt ölme animasyonuna geç
				if (
					this.isInPit &&
					this.animationManager.currentAnimation === 'fall' &&
					!this.isPlayingDeathAnimation
				) {
					this.isPlayingDeathAnimation = true;
					this.takeDamage(1); // Lose 1 health
					this.setAnimation('die', true);
				} else if (
					(this.animationManager.currentAnimation === 'fall' ||
						this.animationManager.currentAnimation === 'jump') &&
					!this.isInPit
				) {
					// Normal yere değdiğinde landing animasyonunu başlat
					this.startLanding();
				}
			}
		}
	}

	private selectAnimation(): void {
		if (this.isPlayingDeathAnimation) {
			// Ölme animasyonu devam ediyor, bölünmemeli
			this.animationManager.currentAnimation = 'die';
		} else if (this.isSleeping) {
			// Sleep durumunda kal, animasyon seçim mantığını atla
			if (this.animationManager.currentAnimation !== 'sleep') {
				this.animationManager.currentAnimation = 'sleep';
				this.animationManager.currentFrame = 0;
				this.animationManager.frameCounter = 0;
			}
		} else if (this.isHurt) {
			this.animationManager.currentAnimation = 'hurt';
		} else if (this.isAttacking) {
			this.animationManager.currentAnimation = 'attack';
		} else if (this.isClimbing) {
			// Animation set in climbing logic above
		} else if (this.isLanding) {
			// Landing animasyonu devam ediyor, bölünmemeli
			this.animationManager.currentAnimation = 'land';
		} else if (this.animationManager.currentAnimation === 'fall' && !this.isGrounded) {
			// Fall animasyonu devam ediyor
			this.animationManager.currentAnimation = 'fall';
		} else if (this.isJumping || !this.isGrounded) {
			this.animationManager.currentAnimation = 'jump';
		} else if (this.isCrouching) {
			if (this.isMoving) {
				this.animationManager.currentAnimation = 'sneak';
			} else {
				this.animationManager.currentAnimation = 'crouch';
			}
		} else if (this.isMoving) {
			this.animationManager.currentAnimation =
				Math.abs(this.vx) > this.speed * 0.7 ? 'run' : 'walk';
		} else if (
			// Eğer hiçbir durum aktif değilse ve mevcut animasyon attack, crouch, sneak, walk, run ise idle'a geç
			this.animationManager.currentAnimation === 'attack' ||
			this.animationManager.currentAnimation === 'crouch' ||
			this.animationManager.currentAnimation === 'sneak' ||
			this.animationManager.currentAnimation === 'walk' ||
			this.animationManager.currentAnimation === 'run'
		) {
			// Bu animasyonlar bittiğinde idle'a geç
			this.animationManager.currentAnimation = 'idle';
		} else {
			// Sleep durumunda değilse normal idle mantığı
			// Sit animasyonu kontrolü (input olmadığında)
			if (this.noInputTimer >= this.NO_INPUT_THRESHOLD_SECONDS) {
				if (
					this.animationManager.currentAnimation === 'idle' ||
					this.animationManager.currentAnimation === 'idle_blink' ||
					this.animationManager.currentAnimation === 'sit'
				) {
					// Sit animasyonu bitmişse veya idle durumundaysa
					if (
						this.animationManager.currentAnimation !== 'sit' ||
						(this.animationManager.currentAnimation === 'sit' &&
							this.animationManager.currentFrame >= this.animationManager.getFrames().length - 1)
					) {
						// Rastgele sit animasyonu tetikle
						if (Math.random() < this.SIT_ANIMATION_CHANCE) {
							this.setAnimation('sit');
							this.sitCount++;
							this.noInputTimer = 0;

							// 3 kez sit olursa sleep'e geç
							if (this.sitCount >= 3) {
								this.isSleeping = true;
								this.setAnimation('sleep');
							}
						}
					}
				}
			}

			// Idle animasyonu: bazen blink varyasyonu
			if (
				this.animationManager.currentAnimation === 'idle' ||
				this.animationManager.currentAnimation === 'idle_blink' ||
				this.animationManager.currentAnimation === 'sit'
			) {
				if (this.animationManager.currentAnimation === 'idle_blink') {
					// Blink animasyonu devam ediyor, bölünmemeli
					this.animationManager.currentAnimation = 'idle_blink';
				} else if (this.animationManager.currentAnimation === 'sit') {
					// Sit animasyonu devam ediyor, bölünmemeli
					this.animationManager.currentAnimation = 'sit';
				} else {
					// Normal idle, blink şansını kontrol et
					this.idleBlinkCounter++;
					if (this.idleBlinkCounter >= this.idleBlinkChance) {
						// Rastgele blink tetikle
						if (Math.random() < 0.3) {
							// %30 şansla blink başlat
							this.animationManager.currentAnimation = 'idle_blink';
							this.animationManager.currentFrame = 0;
							this.animationManager.frameCounter = 0;
							this.idleBlinkCounter = 0;
						} else {
							this.animationManager.currentAnimation = 'idle';
							this.idleBlinkCounter = 0; // Reset counter
						}
					} else {
						this.animationManager.currentAnimation = 'idle';
					}
				}
			}
		}

		// Animasyon seçim mantığından sonra lastAnimation'ı güncelle
		// Bu sayede aynı animasyon tekrar seçildiğinde animasyon değişikliği algılanmaz
		// ve frame counter sürekli resetlenmez
		if (this.animationManager.currentAnimation !== this.animationManager.lastAnimation) {
			this.animationManager.lastAnimation = this.animationManager.currentAnimation;
		}
	}

	private updateAnimationFrame(): void {
		const frames = this.animationManager.getFrames();
		if (frames && frames.length > 0) {
			// Frame'i her zaman geçerli aralıkta tut
			this.animationManager.currentFrame = this.animationManager.currentFrame % frames.length;
			if (
				this.isMoving ||
				this.animationManager.currentAnimation === 'idle' ||
				this.animationManager.currentAnimation === 'idle_blink' ||
				this.animationManager.currentAnimation === 'jump' ||
				this.animationManager.currentAnimation === 'crouch' ||
				this.animationManager.currentAnimation === 'sneak' ||
				this.animationManager.currentAnimation === 'attack' ||
				this.animationManager.currentAnimation === 'hurt' ||
				this.animationManager.currentAnimation === 'wallclimb' ||
				this.animationManager.currentAnimation === 'wallgrab' ||
				this.animationManager.currentAnimation === 'fall' ||
				this.animationManager.currentAnimation === 'land' ||
				this.animationManager.currentAnimation === 'ledgestruggle' ||
				this.animationManager.currentAnimation === 'die' ||
				this.animationManager.currentAnimation === 'sit' ||
				this.animationManager.currentAnimation === 'sleep'
			) {
				let speed =
					this.animationManager.currentAnimation === 'attack'
						? this.attackFrameSpeed
						: this.frameSpeed;
				// Landing animasyonu: ilk frame'den sonra gecikme ekle (düşme hissiyatı için)
				if (this.animationManager.currentAnimation === 'land') {
					if (this.animationManager.currentFrame === 0) {
						// İlk frame'i daha uzun göster (düşme anı)
						speed = this.frameSpeed * Cat.LANDING_DELAY_MULTIPLIER;
					} else {
						// İkinci frame normal hızda (toparlanma)
						speed = this.frameSpeed;
					}
				}
				this.animationManager.frameCounter++;
				if (this.animationManager.frameCounter >= speed) {
					if (this.animationManager.currentAnimation === 'attack') {
						const maxFrame = this.isCrouching ? frames.length - 2 : frames.length - 1;
						if (this.animationManager.currentFrame >= maxFrame) {
							this.isAttacking = false;
							this.animationManager.currentFrame = 0;
							// Animasyon bitince momentum'u sıfırla (eğer yerdeyse)
							if (this.isGrounded) {
								this.vx = 0;
							}
						} else {
							this.animationManager.currentFrame++;
						}
					} else if (this.animationManager.currentAnimation === 'hurt') {
						// Hurt animasyonu bitince idle'a dön
						if (this.animationManager.currentFrame >= frames.length - 1) {
							this.isHurt = false;
							this.animationManager.currentAnimation = 'idle';
							this.animationManager.currentFrame = 0;
						} else {
							this.animationManager.currentFrame++;
						}
					} else {
						// Landing animasyonu kontrolü: son frame'e ulaştığında bir sonraki frame'e geçmeden önce bitir
						if (
							this.animationManager.currentAnimation === 'land' &&
							this.animationManager.currentFrame === frames.length - 1
						) {
							// Son frame'i gösterdik, landing'i bitir
							this.isLanding = false;
							// Eğer çukurun dibindeyse ölme animasyonunu başlat
							if (this.isInPit) {
								this.isPlayingDeathAnimation = true;
								this.takeDamage(1); // Lose 1 health
								this.animationManager.currentAnimation = 'die';
								this.animationManager.currentFrame = 0;
								this.animationManager.frameCounter = 0;
							} else {
								this.animationManager.currentAnimation = 'idle';
								this.animationManager.currentFrame = 0;
							}
						} else if (
							this.animationManager.currentAnimation === 'idle_blink' &&
							this.animationManager.currentFrame === frames.length - 1
						) {
							// Blink animasyonu bitti, idle'e dön
							this.animationManager.currentAnimation = 'idle';
							this.animationManager.currentFrame = 0;
							this.idleBlinkCounter = 0;
						} else if (
							this.animationManager.currentAnimation === 'die' &&
							this.animationManager.currentFrame === frames.length - 1
						) {
							// Death animation complete, stay on last frame
							this.isDeathAnimationComplete = true;
						} else if (this.animationManager.currentAnimation === 'sleep') {
							// Sleep animasyonu loop olmalı
							this.animationManager.currentFrame =
								(this.animationManager.currentFrame + 1) % frames.length;
						} else {
							this.animationManager.currentFrame =
								(this.animationManager.currentFrame + 1) % frames.length;
						}
					}
					this.animationManager.frameCounter = 0;
				}
			}
		}
	}

	private startLanding(): void {
		this.isLanding = true;
		this.setAnimation('land');
		this.vx = 0;
		this.vy = 0;
	}

	/**
	 * Called by Pit.onCollision when cat collides with a pit
	 */
	handlePitCollision(pit: Pit): void {
		const pitBounds = pit.getBounds();

		// Center of gravity check - only fall if the bottom center point is inside pit
		const cog = CollisionUtils.getCenterOfGravity(this.getBounds());
		const isInPitArea =
			cog.x >= pitBounds.x && cog.x <= pitBounds.x + pitBounds.width && cog.y >= pitBounds.y;

		if (!isInPitArea) return; // Not actually in pit yet

		const pitBottom = pitBounds.y + pitBounds.height;

		// Store pit bounds for update() to use
		this.currentPitBounds = pitBounds;

		// Set ground level to pit bottom
		this.groundY = pitBottom - this.spriteHeight;

		// Start falling if above ground
		if (this.y < this.groundY) {
			this.isGrounded = false;
			this.isInPit = true;

			// Start falling animation
			if (
				this.animationManager.currentAnimation !== 'fall' &&
				!this.isLanding &&
				!this.isPlayingDeathAnimation
			) {
				this.startFalling();
			}
		}

		// Reached pit bottom - start death
		if (this.y >= this.groundY && !this.isPlayingDeathAnimation) {
			this.y = this.groundY;
			this.vy = 0;
			this.isGrounded = true;
			this.isInPit = true;

			if (this.animationManager.currentAnimation === 'fall') {
				this.isPlayingDeathAnimation = true;
				this.takeDamage(1);
				this.setAnimation('die');
			}
		}
	}

	/**
	 * Called by Tree.onCollision when cat collides with a tree
	 */
	handleTreeCollision(tree: Tree): void {
		this.activeTree = tree;
		this.isCollidingWithTree = true;

		// Start climbing if in air and not just stopped climbing
		if (!this.isClimbing && !this.isGrounded && !this.justStoppedClimbing) {
			this.startClimbing(tree);
		}
	}

	handleInput(): void {
		if (this.isAttacking || this.isHurt || this.isLanding || this.isPlayingDeathAnimation) {
			return;
		}

		const input = this.inputManager.getInputState();

		// Input varsa timer'ı sıfırla ve sleep durumundan çık
		const hasInput =
			input.moveLeft ||
			input.moveRight ||
			input.moveUp ||
			input.moveDown ||
			input.jump ||
			input.attack ||
			input.poop ||
			input.crouch ||
			Math.abs(input.horizontalAxis) > Cat.ANALOG_DEADZONE ||
			Math.abs(input.verticalAxis) > Cat.ANALOG_DEADZONE;

		if (hasInput) {
			this.noInputTimer = 0;
			if (this.isSleeping) {
				this.isSleeping = false;
				this.sitCount = 0;
				if (this.animationManager.currentAnimation === 'sleep') {
					this.setAnimation('idle');
					this.animationManager.lastAnimation = 'idle';
				}
			}
		} else {
			// Input yoksa timer'ı artır
			const gameTime = container.resolve(GameTime);
			this.noInputTimer += gameTime.deltaTime;
		}

		// Tırmanma kontrolü: zıplama tuşuna basıldığında ve ağaca çarptığında
		if (
			this.activeTree &&
			this.isCollidingWithTree &&
			!this.isClimbing &&
			input.jumpJustPressed &&
			this.isGrounded
		) {
			this.startClimbing(this.activeTree);
			this.canJump = false;
			return; // Tırmanma başladı, normal zıplamayı engelle
		}
		// Climb durumundayken sadece atak ve zıplama çalışır
		if (this.isClimbing) {
			// Crouch varsa tırmanmayı bırak
			if (input.crouch) {
				this.stopClimbing();
				return; // handleInput'u erken bitir
			}

			if (input.attack) {
				this.isAttacking = true;
				this.animationManager.currentFrame = 0;
				this.animationManager.frameCounter = 0;
				this.playSound('attack');
				// return yapma, frame counter'ın çalışması için devam et
			}

			if (!input.jump && this.canJump === false) {
				this.canJump = true;
			}

			if (input.jumpJustPressed && this.canJump) {
				// Zıplama ile tırmanmayı bırak ve düş
				this.stopClimbing();
				this.canJump = false;
			}

			return;
		}

		this.isMoving = false;

		if (input.attack) {
			this.isAttacking = true;
			this.attackMomentum = this.vx; // Store current momentum
			this.animationManager.currentFrame = 0;
			this.animationManager.frameCounter = 0;
			this.playSound('attack');
			// Momentum'u koru (atak sırasında sıfırlama, animasyon bitince sıfırlanacak)
			return;
		}

		// Havadayken vx'i sıfırlama, yerdeyken ve hareket input'u yoksa sıfırla
		// Atak sırasında momentum'u koru (animasyon bitince sıfırlanacak)
		if (this.isGrounded && !this.isAttacking) {
			this.vx = 0;
		}

		this.isCrouching = input.crouch;

		// Determine movement direction and speed
		let moveDirection = 0;
		if (input.moveLeft && !input.moveRight) moveDirection = -1;
		if (input.moveRight && !input.moveLeft) moveDirection = 1;

		// Analog stick overrides keyboard if significant
		const horizontalMagnitude = Math.abs(input.horizontalAxis);
		if (horizontalMagnitude > Cat.ANALOG_DEADZONE) {
			moveDirection = input.horizontalAxis > 0 ? 1 : -1;
			// Analog crouch only if not running
			if (input.crouch && horizontalMagnitude < Cat.RUN_THRESHOLD) {
				this.isCrouching = true;
				this.vx = input.horizontalAxis * this.sneakSpeed;
			} else {
				this.isCrouching = false;
				this.vx = input.horizontalAxis * this.speed;
			}
			this.isMoving = true;
		} else if (moveDirection !== 0) {
			// Keyboard movement
			if (this.isCrouching) {
				this.vx = moveDirection * this.sneakSpeed;
			} else if (input.slowWalk) {
				this.vx = moveDirection * this.speed * Cat.SLOW_WALK_MULTIPLIER;
			} else {
				this.vx = moveDirection * this.speed;
			}
			this.isMoving = true;
		}

		// Update direction if moving
		if (this.isMoving && moveDirection !== 0) {
			this.turn(moveDirection > 0 ? 'east' : 'west');
			// Running cancels crouching
			if (Math.abs(this.vx) > this.speed * Cat.RUN_THRESHOLD) {
				this.isCrouching = false;
			}
		}

		if (!input.jump && this.canJump === false) {
			this.canJump = true;
		}

		if (input.jumpJustPressed && this.isGrounded && !this.isJumping && this.canJump) {
			// Normal zıplama (tırmanma kontrolü update içinde yapılacak)
			let jumpVelocity = this.jumpSpeed;

			if (this.animationManager.currentAnimation === 'idle') {
				jumpVelocity = this.jumpSpeed * 0.5;
			} else if (this.animationManager.currentAnimation === 'run') {
				jumpVelocity = this.jumpSpeed * 1.15;
				if (this.vx !== 0) {
					this.vx = this.vx * 2.5;
				}
			} else if (this.animationManager.currentAnimation === 'walk') {
				jumpVelocity = this.jumpSpeed;
				if (this.vx !== 0) {
					this.vx = this.vx * 1.2;
				}
			}

			this.vy = jumpVelocity;
			this.isJumping = true;
			this.isGrounded = false;
			this.canJump = false;
		}

		// Poop action
		if (input.poopJustPressed && this.onPoop) {
			const offsetX = this.animationManager.direction === 'east' ? -32 : 32;
			this.onPoop(this.x + offsetX, this.y + 8);
		}
	}

	update(): void {
		const input = this.inputManager.getInputState();
		const upPressed = input.moveUp;
		const downPressed = input.moveDown;

		// During attack, preserve momentum for glass collision detection
		if (this.isAttacking && this.isGrounded) {
			this.vx = this.attackMomentum;
		}

		this.updatePitState();

		if (this.updateClimbing(upPressed, downPressed)) return;

		this.updateMovement();
		this.updateGravity();
		this.selectAnimation();
		this.updateAnimationFrame();
	}

	draw(): void {
		const frames = this.animationManager.getFrames();
		// Frame'in varlığını ve geçerliliğini kontrol et
		if (
			frames &&
			frames.length > 0 &&
			this.animationManager.currentFrame >= 0 &&
			this.animationManager.currentFrame < frames.length &&
			frames[this.animationManager.currentFrame]
		) {
			const sprite = frames[this.animationManager.currentFrame];
			// Sprite'ın yüklendiğinden ve geçerli olduğundan emin ol
			if (sprite && sprite.width > 0 && sprite.height > 0) {
				// Image smoothing'i kapat (pixel art için)
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const ctx = (window as any).drawingContext;
				if (ctx) {
					ctx.imageSmoothingEnabled = false;
				}
				image(sprite, this.x, this.y);
			} else {
				console.warn('Geçersiz sprite:', {
					animation: this.animationManager.currentAnimation,
					direction: this.animationManager.direction,
					frame: this.animationManager.currentFrame,
					spriteWidth: sprite?.width,
					spriteHeight: sprite?.height,
				});
			}
		} else {
			console.warn('Geçersiz frame:', {
				animation: this.animationManager.currentAnimation,
				direction: this.animationManager.direction,
				frame: this.animationManager.currentFrame,
				framesLength: frames?.length,
				framesExists: !!frames,
			});
		}

		// Debug collision boxes (Animal base class'tan)
		this.drawDebugCollision();
	}

	override takeDamage(amount: number = 1): void {
		if (this.isPlayingDeathAnimation || this.isDeathAnimationComplete || this.isHurt) {
			return; // Already dying/dead/hurt
		}

		super.takeDamage(amount);

		// Start death animation if health depleted
		if (this.currentHealth <= 0 && !this.isPlayingDeathAnimation) {
			this.isPlayingDeathAnimation = true;
			this.setAnimation('die');
			this.vx = 0;
			this.vy = 0;
		} else {
			// Play hurt animation
			this.isHurt = true;
			this.vx = 0;
			this.vy = 0;
			this.setAnimation('hurt');
		}
	}

	override isActive(): boolean {
		return !this.isPlayingDeathAnimation && !this.isDeathAnimationComplete;
	}

	override onCollision(other: ICollidable): void {
		// Handle collision based on the other object's type
		// Most collision responses are handled by the other object's onCollision
		// This is for Cat-specific responses if needed
		switch (other.collisionType) {
			case 'collectable':
				// Collectable handles heal in its onCollision
				break;
			case 'pit':
				// Pit handles this via handlePitCollision callback
				break;
			// Future: 'enemy', 'powerup', etc.
		}
	}
}
